import React from "react";
import { RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Picking } from "@/types/general";
import { useAbility } from "@/casl.config";
import BaseTooltip from "@reusables/BaseTooltip";
import UIButton from "@reusables/UIButton";
import { useResyncPickingMutation } from "@redux/features/picking/pickingApi";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { isErrorWithMessage } from "@redux/api/query";
import {
    clearPickingResyncError,
    failPickingResync,
    finishPickingResync,
    selectIsPickingResyncing,
    selectPickingResyncErrors,
    startPickingResync,
} from "@redux/features/picking/pickingSlice";
import { isInvoicingIntegrationConnectedSelector } from "@redux/features/auth/authSlice";

type PickingResyncButtonProps = {
    picking: Picking.Root;
};

export default function PickingResyncButton({ picking }: PickingResyncButtonProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.picking.main" });
    const ability = useAbility();
    const dispatch = useAppDispatch();

    const isResyncing = useAppSelector(selectIsPickingResyncing(picking.id));
    const isInvoicingIntegrationConnected = useAppSelector(isInvoicingIntegrationConnectedSelector);

    const errors = useAppSelector(selectPickingResyncErrors(picking.id));

    const [resyncPicking] = useResyncPickingMutation();

    if (
        picking.invoicing.length > 0 ||
        ability.cannot("create", "picking") ||
        !isInvoicingIntegrationConnected
    ) {
        return null;
    }

    const handleResync = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (isResyncing) {
            return;
        }

        dispatch(clearPickingResyncError(picking.id));
        dispatch(startPickingResync(picking.id));

        try {
            await resyncPicking(picking.id).unwrap();
            dispatch(finishPickingResync(picking.id));
        } catch (error) {
            const parsedErrors = parsePickingResyncErrors(
                error,
                t("responses.resyncError") as string
            );

            dispatch(
                failPickingResync({
                    pickingId: picking.id,
                    errors: parsedErrors,
                })
            );
            toast.error(parsedErrors.join(" "));
        }
    };

    const hasError = !!errors?.length;
    const tooltipTitle = hasError ? (
        <div className="max-w-[320px] space-y-1">
            {errors.map((error, index) => (
                <div key={`${error}-${index}`}>{error}</div>
            ))}
        </div>
    ) : isResyncing ? (
        t("actions.resyncing")
    ) : (
        t("actions.resync")
    );

    return (
        <BaseTooltip title={tooltipTitle} placement="top" variant={hasError ? "error" : "default"}>
            <UIButton
                type="button"
                variant={hasError ? "error" : "secondary"}
                size="sm"
                loading={isResyncing}
                aria-label={
                    hasError
                        ? `${t("actions.resync")}. ${errors.join(" ")}`
                        : (t("actions.resync") as string)
                }
                className="h-[26px] w-[26px] !p-0"
                onClick={handleResync}
            >
                <RefreshCcw
                    size={14}
                    aria-hidden
                    className="picking-resync-icon"
                    style={
                        isResyncing
                            ? {
                                  animation: "picking-resync-rotation 1s linear infinite",
                              }
                            : undefined
                    }
                />
            </UIButton>
        </BaseTooltip>
    );
}

function parsePickingResyncErrors(error: unknown, fallbackMessage: string): string[] {
    const validationErrors = getPickingValidationErrors(error);

    if (validationErrors.length > 0) {
        return validationErrors;
    }

    if (isErrorWithMessage(error)) {
        return [error.message];
    }

    if (error instanceof Error) {
        return [error.message];
    }

    return [fallbackMessage];
}

function getPickingValidationErrors(error: unknown): string[] {
    if (!isErrorWithMessage(error)) {
        return [];
    }

    const responseData = error.__origin.response?.data as unknown;

    if (!isRecord(responseData) || !isRecord(responseData.errors)) {
        return [];
    }

    const pickingErrors = responseData.errors.picking;

    return Array.isArray(pickingErrors) &&
        pickingErrors.every((message): message is string => typeof message === "string")
        ? pickingErrors
        : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}
